import { Button, Stack, Typography } from '@mui/material'
import {
  ProductMasterBatchJobOperationTypeEnum,
  ProductMasterBatchJobStatusEnum,
} from '@ulysses-inc/harami_api_client'
import { format } from 'date-fns'
import React, { useCallback } from 'react'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import StatusChip from 'src/features/masters/components/LatestBatchJobStatusDrawer/components/BatchJobDetailsContent/components/StatusChip'
import { BatchJobData } from 'src/features/masters/components/LatestBatchJobStatusDrawer/components/type'
import { useMutationGenerateMasterBatchJobErrorDetailDownloadUrl } from 'src/features/masters/MasterDetail/api/useMutationGenerateMasterBatchJobErrorDetailDownloadUrl'
import { isHTTPError } from 'src/state/middleware/saga/handleHttpError'

type Props = {
  masterUuid: string
  data: BatchJobData
}

const formatDateTime = (date: Date): string =>
  format(date, 'yyyy/MM/dd HH:mm:ss')

const getOperationTypeLabel = (
  operationType: ProductMasterBatchJobOperationTypeEnum,
): string => {
  switch (operationType) {
    case ProductMasterBatchJobOperationTypeEnum.Upsert:
      return '追加・更新'
    case ProductMasterBatchJobOperationTypeEnum.Delete:
      return '削除'
    // API 側で新しい操作種別が追加された場合に備え、ExhaustiveError ではなくフォールバックで表示する
    default:
      return '不明'
  }
}

const downloadFile = async (url: string, filename: string) => {
  const res = await fetch(url)
  if (!res.ok) {
    throw new Error(`Download failed with status ${res.status}`)
  }
  const objectUrl = URL.createObjectURL(await res.blob())
  try {
    const aTag = document.createElement('a')
    aTag.href = objectUrl
    aTag.download = filename
    document.body.appendChild(aTag)
    aTag.click()
    document.body.removeChild(aTag)
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

const MasterBatchJobDetailsContent: React.FC<Props> = ({
  masterUuid,
  data,
}) => {
  const { showErrorSnackbar } = useSnackbar()
  const mutation = useMutationGenerateMasterBatchJobErrorDetailDownloadUrl()

  const handleDownload = useCallback(async () => {
    try {
      const { downloadUrl } = await mutation.mutateAsync({
        masterUuid,
        jobUuid: data.jobUuid,
      })
      await downloadFile(downloadUrl, 'master_error.csv')
    } catch (error) {
      if (isHTTPError(error) && error.status === 410) {
        showErrorSnackbar(
          '期限切れのためダウンロードできません。再度アップロードを行ってください',
        )
        return
      }
      showErrorSnackbar(
        'ダウンロードに失敗しました。再度ダウンロードを行ってください',
      )
    }
  }, [data, masterUuid, mutation, showErrorSnackbar])

  return (
    <Stack sx={{ flex: 1 }} padding={3}>
      <Stack gap={3} padding={1}>
        <Stack gap={1}>
          <Typography variant="body1" fontWeight="bold">
            CSVファイル名
          </Typography>
          <Typography variant="body1">{data.fileName}</Typography>
        </Stack>

        <Stack gap={1}>
          <Typography variant="body1" fontWeight="bold">
            操作
          </Typography>
          <Typography variant="body1">
            {getOperationTypeLabel(data.operationType)}
          </Typography>
        </Stack>

        <Stack gap={1}>
          <Typography variant="body1" fontWeight="bold">
            ステータス
          </Typography>
          <Stack direction="row">
            <StatusChip status={data.status} />
          </Stack>
        </Stack>

        <Stack gap={1}>
          <Typography variant="body1" fontWeight="bold">
            アップロード日時
          </Typography>
          <Typography variant="body1">
            {formatDateTime(data.createdAt)}
          </Typography>
        </Stack>

        <Stack gap={1}>
          <Typography variant="body1" fontWeight="bold">
            実行ユーザー表示名（ユーザー管理番号）
          </Typography>
          <Typography variant="body1">
            {`${data.uploaderUserDisplayName} (${data.uploaderRumpCustomerMemberId})`}
          </Typography>
        </Stack>

        {data.status === ProductMasterBatchJobStatusEnum.Error && (
          <Stack gap={1}>
            <Typography variant="body1" fontWeight="bold">
              エラー内容
            </Typography>
            <Typography variant="body1">{data.errorMessage}</Typography>
            <Stack direction="row">
              <Button
                variant="outlined"
                size="medium"
                color="primary"
                onClick={handleDownload}
                disabled={mutation.isPending}
              >
                {mutation.isPending
                  ? 'ダウンロード中...'
                  : 'エラー詳細CSVダウンロード'}
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}

export default MasterBatchJobDetailsContent
